/**
 * Formats a date string into a human-readable format
 * @param dateString - Date string in ISO format or Date object
 * @returns Formatted date string (e.g., "Mar 8, 2025")
 */
export function formatDate(dateString: string | Date): string {
  if (!dateString) return 'N/A'

  const date = typeof dateString === 'string' ? new Date(dateString) : dateString

  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(date)
}

/**
 * Formats a date range into a human-readable format
 * @param startDate - Start date string in ISO format or Date object
 * @param endDate - End date string in ISO format or Date object
 * @returns Formatted date range string
 */
export function formatDateRange(startDate: string | Date, endDate: string | Date): string {
  if (!startDate || !endDate) return 'N/A'

  const start = typeof startDate === 'string' ? new Date(startDate) : startDate

  const end = typeof endDate === 'string' ? new Date(endDate) : endDate

  // If same day, show as "Mar 8, 2025"
  if (start.toDateString() === end.toDateString()) {
    return formatDate(start)
  }

  // If same month and year, show as "Mar 8-10, 2025"
  if (start.getMonth() === end.getMonth() && start.getFullYear() === end.getFullYear()) {
    return `${start.toLocaleString('en-US', {
      month: 'short',
    })} ${start.getDate()}-${end.getDate()}, ${start.getFullYear()}`
  }

  // If same year, show as "Mar 8 - Apr 10, 2025"
  if (start.getFullYear() === end.getFullYear()) {
    return `${start.toLocaleString('en-US', {
      month: 'short',
    })} ${start.getDate()} - ${end.toLocaleString('en-US', {
      month: 'short',
    })} ${end.getDate()}, ${start.getFullYear()}`
  }

  // Otherwise, show full dates "Mar 8, 2025 - Apr 10, 2026"
  return `${formatDate(start)} - ${formatDate(end)}`
}

/**
 * Formats a time string into a human-readable format
 * @param timeString - Time string in ISO format or Date object
 * @returns Formatted time string (e.g., "2:30 PM")
 */
export function formatTime(timeString: string | Date): string {
  if (!timeString) return 'N/A'

  const date = typeof timeString === 'string' ? new Date(timeString) : timeString

  return new Intl.DateTimeFormat('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  }).format(date)
}

/**
 * Formats a datetime string into a human-readable format
 * @param dateTimeString - Datetime string in ISO format or Date object
 * @returns Formatted datetime string (e.g., "Mar 8, 2025, 2:30 PM")
 */
export function formatDateTime(dateTimeString: string | Date): string {
  if (!dateTimeString) return 'N/A'

  const date = typeof dateTimeString === 'string' ? new Date(dateTimeString) : dateTimeString

  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  }).format(date)
}

/**
 * Formats a datetime range into a human-readable format with time
 * @param startDateTime - Start datetime string in ISO format or Date object
 * @param endDateTime - End datetime string in ISO format or Date object
 * @returns Formatted datetime range string
 */
export function formatDateTimeRange(
  startDateTime: string | Date,
  endDateTime: string | Date
): string {
  if (!startDateTime || !endDateTime) return 'N/A'

  const start = typeof startDateTime === 'string' ? new Date(startDateTime) : startDateTime

  const end = typeof endDateTime === 'string' ? new Date(endDateTime) : endDateTime

  // If same day, show as "Mar 8, 2025, 2:30 PM - 4:30 PM"
  if (start.toDateString() === end.toDateString()) {
    return `${formatDate(start)}, ${formatTime(start)} - ${formatTime(end)}`
  }

  // Otherwise, show full datetime range "Mar 8, 2025, 2:30 PM - Mar 9, 2025, 4:30 PM"
  return `${formatDateTime(start)} - ${formatDateTime(end)}`
}
